import type { SpeechModel } from 'ai';
import { createFibonacciDelays, sleep } from '@flowmuse/models/shared/backoff';
import { createScopedLogger } from '@flowmuse/models/shared/debug-log';
import { nanoid } from 'nanoid';

const CREATE_RETRY_DELAY_MS = 500;
const POLL_RETRY_ATTEMPTS = 3;
const POLL_RETRY_DELAY_MS = 1000;

function isRetryableNetworkStatus(status: number): boolean {
  return status === 408 || status === 429 || (status >= 500 && status <= 599);
}

function extractErrorDetail(data: any): string {
  if (!data) return '';
  const parts: string[] = [];
  const code = data?.error?.code;
  if (typeof code === 'string' && code) parts.push(code);
  const msg = data?.error?.message || data?.message || data?.msg || data?.detail;
  if (typeof msg === 'string' && msg) parts.push(msg);
  if (!parts.length && typeof data?.error === 'string' && data.error) parts.push(data.error);
  return parts.join(': ');
}

function isAbortError(error: unknown): boolean {
  return (
    error instanceof Error &&
    (error.name === 'AbortError' || error.message.toLowerCase().includes('abort'))
  );
}

// ==================== 任务创建 ====================
export type CreateTaskConfig = {
  url: string;
  method: 'GET' | 'POST' | 'PUT';
  headers: Record<string, string>;
  body?: Record<string, any> | string;
};

export type CreateTaskResult = {
  taskId?: string;
  /** 同步模型：创建阶段直接返回音频 URL */
  immediateAudioUrls?: string[];
  error?: string;
  raw?: any;
};

// ==================== 任务轮询 ====================
export type PollTaskConfig = {
  url: string | ((taskId: string) => string);
  method: 'GET' | 'POST';
  headers: Record<string, string>;
  body?: Record<string, any> | string;
};

export type PollingConfig = {
  timeoutMs?: number;
  backoff?: {
    initialDelayMs?: number;
    maxSteps?: number;
    scale?: number;
    jitter?: number;
    maxDelayMs?: number;
  };
};

export enum TaskStatus {
  PROCESSING = 'processing',
  SUCCESS = 'success',
  FAILED = 'failed',
}

export type PollTaskResult = {
  status: TaskStatus;
  audioUrls?: string[];
  mimeType?: string;
  error?: string;
  raw?: any;
  providerMetadata?: any;
};

// ==================== Speech 模型生成参数 ====================
export type SpeechGenerateParams = {
  text: string;
  voice?: string;
  outputFormat?: string;
  instructions?: string;
  providerOptions?: Record<string, any>;
  abortSignal?: AbortSignal;
};

// ==================== 提供商配置 ====================
export type ProviderConfig = {
  baseUrlEnv: string;
  apiKeyEnv: string;
  debugEnv?: string;
  providerName: string;
};

export type ProviderContext = {
  baseUrl: string;
  apiKey: string;
};

export interface SpeechProviderAdapter {
  buildCreateTaskRequest(
    modelId: string,
    params: SpeechGenerateParams,
    context: ProviderContext
  ): CreateTaskConfig | Promise<CreateTaskConfig>;

  parseCreateTaskResponse(
    modelId: string,
    response: Response,
    data: any
  ): CreateTaskResult;

  buildPollTaskRequest(
    modelId: string,
    taskId: string,
    params: SpeechGenerateParams,
    context: ProviderContext
  ): PollTaskConfig;

  parsePollTaskResponse(
    modelId: string,
    response: Response,
    data: any
  ): PollTaskResult;
};

export class BaseSpeechProvider {
  protected config: ProviderConfig;
  protected pollingConfig: PollingConfig;
  protected debug: boolean;

  constructor(config: ProviderConfig, pollingConfig: PollingConfig = {}) {
    this.config = config;
    this.pollingConfig = {
      timeoutMs: 10 * 60 * 1000,
      backoff: {
        initialDelayMs: 5000,
        maxSteps: 20,
        scale: 1,
        jitter: 0.2,
        maxDelayMs: 10000,
      },
      ...pollingConfig,
    };
    this.debug =
      process.env[config.debugEnv || `${config.providerName.toUpperCase()}_DEBUG`] ===
      'true';
  }

  protected getBaseUrl(): string {
    const base = process.env[this.config.baseUrlEnv];
    if (!base) throw new Error(`Missing ${this.config.baseUrlEnv}`);
    return base.replace(/\/$/, '');
  }

  protected getApiKey(): string {
    const key = process.env[this.config.apiKeyEnv];
    if (!key) throw new Error(`Missing ${this.config.apiKeyEnv}`);
    return key;
  }

  protected createLogger(modelId: string, requestId: string) {
    return createScopedLogger({
      tags: [this.config.providerName.toUpperCase(), modelId, requestId],
      debug: this.debug,
    });
  }

  protected async executeRequest(
    config: CreateTaskConfig | PollTaskConfig,
    abortSignal?: AbortSignal
  ): Promise<{ response: Response; data: any }> {
    const url = typeof config.url === 'function' ? config.url('') : config.url;
    const createConfig = config as CreateTaskConfig;
    const method = config.method;
    const headers = config.headers;

    let body: string | undefined;
    if (createConfig.body !== undefined) {
      body =
        typeof createConfig.body === 'string'
          ? createConfig.body
          : JSON.stringify(createConfig.body);
    }

    const response = await fetch(url, {
      method,
      headers,
      body,
      signal: abortSignal,
    });

    let data: any;
    try {
      const text = await response.text();
      data = text ? JSON.parse(text) : {};
    } catch {
      data = {};
    }

    return { response, data };
  }

  /**
   * 仅创建任务（含创建阶段重试与解析），返回 taskId 或同步音频 URL。
   * 供 Inngest 等编排层按「短 step」方式单独调用，避免把长耗时轮询塞进单个请求导致网关超时(524)。
   */
  async createTask(
    modelId: string,
    adapter: SpeechProviderAdapter,
    params: SpeechGenerateParams
  ): Promise<CreateTaskResult> {
    const requestId = nanoid(10);
    const log = this.createLogger(modelId, requestId);
    const context: ProviderContext = {
      baseUrl: this.getBaseUrl(),
      apiKey: this.getApiKey(),
    };

    log.debug('[create:start]', { hasProviderOptions: !!params.providerOptions });
    const createConfig = await Promise.resolve(
      adapter.buildCreateTaskRequest(modelId, params, context)
    );

    let createResp: Response | undefined;
    let createData: any = undefined;

    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const result = await this.executeRequest(createConfig, params.abortSignal);
        createResp = result.response;
        createData = result.data;

        if (!createResp.ok) {
          log.error('[create:http_error]', {
            attempt: attempt + 1,
            status: createResp.status,
            statusText: createResp.statusText,
            data: createData,
          });

          if (attempt === 0 && isRetryableNetworkStatus(createResp.status)) {
            log.error('[create:retry_once]', { reason: 'retryable_status', status: createResp.status });
            await sleep(CREATE_RETRY_DELAY_MS);
            continue;
          }

          const detail = extractErrorDetail(createData);
          throw new Error(
            `${modelId} task create failed: ${createResp.status} ${createResp.statusText}${detail ? ` — ${detail}` : ''}`
          );
        }

        break;
      } catch (error) {
        if (attempt === 0 && !isAbortError(error)) {
          log.error('[create:request_error]', {
            attempt: attempt + 1,
            error: error instanceof Error ? error.message : String(error),
          });
          log.error('[create:retry_once]', { reason: 'fetch_error' });
          await sleep(CREATE_RETRY_DELAY_MS);
          continue;
        }
        throw error;
      }
    }

    if (!createResp) {
      throw new Error(`${modelId} task create failed: missing response`);
    }

    const createResult = adapter.parseCreateTaskResponse(modelId, createResp, createData);
    if (createResult.error) {
      log.error('[create:failed]', { error: createResult.error, data: createResult.raw });
      throw new Error(createResult.error);
    }
    if (!createResult.immediateAudioUrls?.length && !createResult.taskId) {
      log.error('[create:missing_task_id]', { data: createResult.raw });
      throw new Error('Failed to get task id');
    }

    log.debug('[create:success]', { taskId: createResult.taskId });
    // 丢弃可能很大的 raw（避免被 Inngest step 结果序列化记忆）
    return {
      taskId: createResult.taskId,
      immediateAudioUrls: createResult.immediateAudioUrls,
    };
  }

  /**
   * 单次轮询（含 poll 请求层重试），返回任务状态。不在内部 sleep —
   * 轮询节奏由编排层（如 Inngest step.sleep）控制。
   */
  async pollTaskOnce(
    modelId: string,
    adapter: SpeechProviderAdapter,
    taskId: string,
    params: SpeechGenerateParams
  ): Promise<PollTaskResult> {
    const requestId = nanoid(10);
    const log = this.createLogger(modelId, requestId);
    const context: ProviderContext = {
      baseUrl: this.getBaseUrl(),
      apiKey: this.getApiKey(),
    };

    const pollConfig = adapter.buildPollTaskRequest(modelId, taskId, params, context);
    const pollUrl =
      typeof pollConfig.url === 'function' ? pollConfig.url(taskId) : pollConfig.url;

    let pollResp: Response | undefined;
    let pollData: any = undefined;
    for (let attempt = 0; attempt < POLL_RETRY_ATTEMPTS; attempt++) {
      try {
        const result = await this.executeRequest(
          { ...pollConfig, url: pollUrl },
          params.abortSignal
        );
        pollResp = result.response;
        pollData = result.data;

        if (!pollResp.ok) {
          log.error('[poll:http_error]', {
            attempt: attempt + 1,
            status: pollResp.status,
            statusText: pollResp.statusText,
            data: pollData,
          });

          if (attempt < POLL_RETRY_ATTEMPTS - 1 && isRetryableNetworkStatus(pollResp.status)) {
            log.error('[poll:retry]', { reason: 'retryable_status', status: pollResp.status, attempt: attempt + 1 });
            await sleep(POLL_RETRY_DELAY_MS);
            continue;
          }

          const detail = extractErrorDetail(pollData);
          throw new Error(
            `${modelId} task poll failed: ${pollResp.status} ${pollResp.statusText}${detail ? ` — ${detail}` : ''}`
          );
        }

        break;
      } catch (error) {
        if (isAbortError(error)) throw error;
        log.error('[poll:request_error]', {
          attempt: attempt + 1,
          error: error instanceof Error ? error.message : String(error),
        });
        if (attempt < POLL_RETRY_ATTEMPTS - 1) {
          log.error('[poll:retry]', { reason: 'fetch_error', attempt: attempt + 1 });
          await sleep(POLL_RETRY_DELAY_MS);
          continue;
        }
        throw error;
      }
    }

    if (!pollResp) {
      throw new Error(`${modelId} task poll failed: missing response`);
    }

    const pollResult = adapter.parsePollTaskResponse(modelId, pollResp, pollData);
    // 丢弃可能很大的 raw
    return {
      status: pollResult.status,
      audioUrls: pollResult.audioUrls,
      mimeType: pollResult.mimeType,
      error: pollResult.error,
      providerMetadata: pollResult.providerMetadata,
    };
  }

  /**
   * 下载音频 URL 列表为 buffer 并构造 AI SDK doGenerate 的返回结果。
   * 供 doGenerate（同步等待模式）复用，避免与轮询逻辑重复。
   */
  protected async buildGenerateResult(
    modelId: string,
    urls: string[],
    abortSignal?: AbortSignal,
    knownMimeType?: string,
    providerMetadata?: any
  ): Promise<any> {
    const buffers: Uint8Array[] = [];
    let mimeType: string | undefined = knownMimeType;

    for (const url of urls) {
      const audioResp = await fetch(url, { signal: abortSignal });
      if (!audioResp.ok) {
        throw new Error(`${modelId} task failed: failed to fetch audio`);
      }
      mimeType = mimeType || audioResp.headers.get('content-type') || 'audio/mpeg';
      const buffer = new Uint8Array(await audioResp.arrayBuffer());
      if (buffer.length === 0) {
        throw new Error(`${modelId} task failed: failed to convert audio to buffer`);
      }
      buffers.push(buffer);
    }

    const extraMeta =
      providerMetadata || urls.length > 1
        ? { providerMetadata: { ...providerMetadata, ...(urls.length > 1 ? { audioUrls: urls } : {}) } }
        : {};

    return {
      audio: buffers[0],
      audios: buffers,
      mimeType: mimeType || 'audio/mpeg',
      warnings: [],
      response: { timestamp: new Date(), modelId, headers: undefined },
      ...extraMeta,
    };
  }

  createSpeechModel(
    modelId: string,
    adapter: SpeechProviderAdapter,
    modelTimeoutMs?: number
  ): SpeechModel {
    const model: any = {
      specificationVersion: 'v1',
      provider: this.config.providerName.toLowerCase(),
      modelId,
      // 同步等待模式：内部完成 create + 轮询 + 下载，会阻塞至任务完成。
      // 注意：长耗时任务请改用 createTask/pollTask 分步编排（见 Inngest），否则可能触发网关超时(524)。
      doGenerate: async (params: SpeechGenerateParams) => {
        const requestId = nanoid(10);
        const log = this.createLogger(modelId, requestId);
        // 每次调用使用独立的延迟序列，避免实例级共享状态在并发请求间互相干扰。
        const nextDelay = createFibonacciDelays(this.pollingConfig.backoff);

        try {
          const created = await this.createTask(modelId, adapter, params);

          // 同步模型：创建即返回音频 URL
          if (created.immediateAudioUrls?.length) {
            return await this.buildGenerateResult(modelId, created.immediateAudioUrls, params.abortSignal);
          }

          const taskId = created.taskId!;
          const startTime = Date.now();
          const timeoutMs = modelTimeoutMs ?? this.pollingConfig.timeoutMs!;
          let iteration = 0;

          while (Date.now() - startTime < timeoutMs) {
            await sleep(nextDelay());
            iteration++;

            const pollResult = await this.pollTaskOnce(modelId, adapter, taskId, params);
            log.debug('[poll:request]', { iteration, taskId, status: pollResult.status });

            if (pollResult.status === TaskStatus.FAILED) {
              throw new Error(pollResult.error || 'unknown error');
            }
            if (pollResult.status === TaskStatus.SUCCESS) {
              const urls = pollResult.audioUrls || [];
              if (!urls.length) {
                throw new Error(`${modelId} task generate failed: missing audio URLs`);
              }
              return await this.buildGenerateResult(
                modelId,
                urls,
                params.abortSignal,
                pollResult.mimeType,
                pollResult.providerMetadata
              );
            }
          }

          throw new Error('Timeout');
        } catch (error) {
          log.error('[generate:error]', {
            error: error instanceof Error ? error.message : String(error),
          });
          throw error;
        }
      },
    };

    // 暴露分步任务接口，供编排层（Inngest）以「短 step」方式 create + 轮询，避免长请求触发网关超时(524)。
    model.createTask = (params: SpeechGenerateParams) =>
      this.createTask(modelId, adapter, params);
    model.pollTask = (taskId: string, params: SpeechGenerateParams) =>
      this.pollTaskOnce(modelId, adapter, taskId, params);
    model.pollingMeta = {
      timeoutMs: modelTimeoutMs ?? this.pollingConfig.timeoutMs!,
    };

    return model as SpeechModel;
  }
}

/** 附加在 SpeechModel 上的分步任务接口（仅异步任务型 provider 提供）。 */
export type SpeechAsyncTaskModel = SpeechModel & {
  createTask: (params: SpeechGenerateParams) => Promise<CreateTaskResult>;
  pollTask: (taskId: string, params: SpeechGenerateParams) => Promise<PollTaskResult>;
  pollingMeta: { timeoutMs: number };
};

export function getSpeechAsyncTask(model: SpeechModel): SpeechAsyncTaskModel | null {
  const m = model as any;
  return typeof m?.createTask === 'function' && typeof m?.pollTask === 'function'
    ? (m as SpeechAsyncTaskModel)
    : null;
}
